import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { User, Mail, Lock, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { useApp } from "../context/AppContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Auth() {
  const { login } = useApp();
  const [isLogin, setIsLogin] = useState(true);
  const [referrerName, setReferrerName] = useState("");
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    referral_code: "",
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get("ref");
    if (ref) {
      setFormData(prev => ({ ...prev, referral_code: ref }));
      setIsLogin(false); // Switch to register mode if referral link used
      fetchReferrerName(ref);
    }
  }, [location]);

  const fetchReferrerName = async (code) => {
    if (!code) return;
    try {
      // First check if referral program is enabled
      const settingsRes = await axios.get(`${API}/settings`);
      if (settingsRes.data.referral_enabled === false) {
        return;
      }
      
      const response = await axios.get(`${API}/auth/referrer/${code}`);
      setReferrerName(response.data.name);
    } catch (error) {
      console.error("Failed to fetch referrer name:", error);
      setReferrerName("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? "/auth/login" : "/auth/register";
      const payload = isLogin
        ? { email: formData.email, password: formData.password }
        : formData;
      const response = await axios.post(`${API}${endpoint}`, payload);

      if (isLogin) {
        login(response.data.user, response.data.token);
      }

      toast.success(
        isLogin ? "Logged in successfully!" : "Account created successfully!"
      );
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 pt-20"
      data-testid="auth-page"
    >
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        <div className="glass-morphic rounded-2xl p-8 space-y-6">
          <div className="text-center space-y-2">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/30 text-primary text-sm font-mono tracking-wider uppercase mb-4">
              <Sparkles className="h-4 w-4" />
              {isLogin ? "Welcome Back" : "Join Us"}
            </div>
            {referrerName && !isLogin && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="mb-4 p-3 rounded-xl bg-primary/10 border border-primary/20 text-primary text-sm font-medium"
              >
                🎁 You were invited by <span className="font-bold">{referrerName}</span>
              </motion.div>
            )}
            <h2 className="text-3xl font-bold" data-testid="auth-title">
              {isLogin ? "Login" : "Create Account"}
            </h2>
            <p className="text-muted-foreground">
              Enter your details to continue
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="space-y-4"
            data-testid="auth-form"
          >
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="name" className="text-foreground">
                  Name
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="pl-10 bg-white/5 border-white/10 focus:border-primary/50 focus:ring-primary/20 h-12"
                    required={!isLogin}
                    data-testid="name-input"
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-foreground">
                Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="pl-10 bg-white/5 border-white/10 focus:border-primary/50 focus:ring-primary/20 h-12"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-foreground">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  className="pl-10 bg-white/5 border-white/10 focus:border-primary/50 focus:ring-primary/20 h-12"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>

            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="referral_code" className="text-foreground">
                  Referral Code (Optional)
                </Label>
                <div className="relative">
                  <Sparkles className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="referral_code"
                    type="text"
                    placeholder="Enter referral code"
                    value={formData.referral_code}
                    onChange={(e) =>
                      setFormData({ ...formData, referral_code: e.target.value })
                    }
                    onBlur={(e) => fetchReferrerName(e.target.value)}
                    className="pl-10 bg-white/5 border-white/10 focus:border-primary/50 focus:ring-primary/20 h-12"
                  />
                </div>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-primary hover:bg-primary/90 text-white py-6 text-lg shadow-[0_0_20px_rgba(139,92,246,0.3)] hover:shadow-[0_0_30px_rgba(139,92,246,0.5)] transition-all duration-300"
              data-testid="submit-button"
            >
              {loading
                ? "Please wait..."
                : isLogin
                ? "Login"
                : "Create Account"}
            </Button>
          </form>

          <div className="text-center space-y-4">
            {isLogin && (
              <Link
                to="/forgot-password"
                className="block text-sm text-muted-foreground hover:text-primary transition-colors duration-300"
                data-testid="forgot-password-link"
              >
                Forgot your password?
              </Link>
            )}

            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-sm text-muted-foreground hover:text-primary transition-colors duration-300"
              data-testid="toggle-auth-mode"
            >
              {isLogin
                ? "Don't have an account? Sign up"
                : "Already have an account? Login"}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
